import type { AnnualMetrics } from '@/types/stock';
import { formatCurrency, formatPercent, formatNumber } from '@/lib/analysisEngine';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface Props {
  metrics: AnnualMetrics[];
}

interface MetricRow {
  label: string;
  field: keyof AnnualMetrics;
  format: 'currency' | 'percent' | 'ratio' | 'number';
  target?: { value: number; direction: 'above' | 'below' };
  growthField?: keyof AnnualMetrics;
}

const METRIC_ROWS: MetricRow[] = [
  { label: 'Revenue', field: 'revenue', format: 'currency', growthField: 'revenueGrowth' },
  { label: 'Gross Profit', field: 'grossProfit', format: 'currency', growthField: 'grossProfitGrowth' },
  { label: 'Net Income', field: 'netIncome', format: 'currency' },
  { label: 'EPS (Diluted)', field: 'eps', format: 'number', growthField: 'epsGrowth' },
  { label: 'Gross Margin', field: 'grossMargin', format: 'percent', target: { value: 30, direction: 'above' } },
  { label: 'Operating Margin', field: 'operatingMargin', format: 'percent' },
  { label: 'Net Profit Margin', field: 'profitMargin', format: 'percent', target: { value: 15, direction: 'above' } },
  { label: 'FCF Margin', field: 'fcfMargin', format: 'percent' },
  { label: 'Free Cash Flow', field: 'freeCashFlow', format: 'currency' },
  { label: 'Current Ratio', field: 'currentRatio', format: 'ratio', target: { value: 1, direction: 'above' } },
  { label: 'Debt/Equity', field: 'debtToEquity', format: 'ratio', target: { value: 2, direction: 'below' } },
];

function formatVal(value: number | null, format: MetricRow['format']): string {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'currency': return formatCurrency(value);
    case 'percent': return formatPercent(value);
    case 'ratio': return formatNumber(value, 2) + 'x';
    case 'number': return formatNumber(value, 2);
    default: return String(value);
  }
}

function GrowthIndicator({ value }: { value: number | null }) {
  if (value === null || value === undefined) return null;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 text-xs ml-1',
      value > 0 ? 'text-emerald-600 dark:text-emerald-400' : value < 0 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground',
    )}>
      <Icon className="w-3 h-3" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function getCellClass(
  value: number | null,
  target: MetricRow['target'],
): string {
  if (value === null || !target) return '';
  const passes = target.direction === 'above' ? value >= target.value : value <= target.value;
  return passes
    ? 'text-emerald-700 dark:text-emerald-400 font-medium'
    : 'text-red-600 dark:text-red-400';
}

export function MetricsTable({ metrics }: Props) {
  const years = metrics.map((m) => m.fiscalYear);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-40 sticky left-0 bg-background">Metric</TableHead>
            {years.map((y) => (
              <TableHead key={y} className="text-right min-w-[90px]">
                {y}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {METRIC_ROWS.map((row) => (
            <TableRow key={row.field}>
              <TableCell className="font-medium text-sm sticky left-0 bg-background">
                {row.label}
              </TableCell>
              {metrics.map((m) => {
                const rawValue = m[row.field];
                const value = typeof rawValue === 'number' ? rawValue : null;
                const growthRaw = row.growthField ? m[row.growthField] : null;
                const growth = typeof growthRaw === 'number' ? growthRaw : null;
                return (
                  <TableCell
                    key={m.fiscalYear}
                    className={cn('text-right text-sm', getCellClass(value, row.target))}
                  >
                    {formatVal(value, row.format)}
                    {growth !== null && <GrowthIndicator value={growth} />}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
